import EnhancedCircularLoader from "../../../../../../../components/EnhancedCircularLoader";
import DataBox from "./DataBox";
import React, {useEffect} from "react";
import {keptSyncStatuses, retailerColorMap, StatBox} from "../dashboard";
import axios from "axios";
import {retailerCatalogSearch} from "../../../catalog";
import { Button, Chip, Grid, Link, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import {getCatalogSyncHistories, utilGetCatalogEvents, utilTriggerWorkflowForPartner} from "../../../../../../../utils/utils";
import cronstrue from "cronstrue";
import { Alert } from '@mui/material';
import GenericDialog from "../../../../../../../components/Dialogs/GenericDialog";

// region TypeScript Interfaces
interface DisableLinkProps {
    disabled?: boolean;
    children?: React.ReactNode;
    href?: string;
    style?: React.CSSProperties;
    color?: any;
    component?: any;
    underline?: any;
}

interface ConnectorStatBoxProps {
    profiles: any[];
    loading?: boolean;
    connectorId: number | string;
    availableRetailers: any[];
    setError: (err: { open: boolean; errorHeader: string; errorMessage: string }) => void;
}

type ConnectorListingData = Record<string, number>;
// endregion

let cancelSource = axios.CancelToken.source();

const DisableLink = (props: DisableLinkProps) => {
    if (props.disabled) {
        return (
            <Typography style={{color: "gray", fontSize: "18px"}}>
                {props.children}
            </Typography>
        );
    }

    return (
        <Link {...(props as any)}>
            <Typography style={{fontSize: "18px"}}>
                {props.children}
            </Typography>
        </Link>
    );
}

const MiniText = styled(Typography)(({ theme }: any) => ({
    fontSize: "12px",
    color: "gray",
    fontWeight: "bold"
}));


export default function ConnectorStatBox(props: ConnectorStatBoxProps) {
    const {profiles, loading, connectorId, availableRetailers, setError} = props;

    const [connectorListingData, setConnectorListingData] = React.useState<ConnectorListingData>({});
    const [schedules, setSchedules] = React.useState<any[]>([]);
    const [connectorLoading, setConnectorLoading] = React.useState(true);
    const [isLive, setIsLive] = React.useState(false);

    const [firingSync, setFiringSync] = React.useState(false);
    const [didFireSync, setDidFireSync] = React.useState(false);

    const connectorName = availableRetailers.find((retailer: any) => retailer.Id === connectorId)?.CompanyName ?? "Unknown";
    const connectorProfile = profiles.find((profile: any) => profile.Partner === connectorId);

    const fireSync = async () => {
        setFiringSync(true);
        const response = await utilTriggerWorkflowForPartner(connectorId);
        if (!response.success) {
            setError({open: true, errorHeader: "Error sending manual sync", errorMessage: response.error});
        }
        setFiringSync(false);
        setDidFireSync(true);
    }

    useEffect(() => {
        const loadPreviousImport = async (profile: any) => {
            if (!profile) return;
            try {
                let eventsResponse = await utilGetCatalogEvents(profile.Partner, cancelSource);

                if (!eventsResponse.success) {
                    setError({open: true, errorHeader: "Error", errorMessage: eventsResponse.error});
                    return;
                } else {
                    setIsLive(eventsResponse.success && eventsResponse.data === null);
                }
            } catch (e) {
                setError({open: true, errorHeader: "Error", errorMessage: "Error loading previous import file."});
            }
        }

        const getSchedules = async () => {
            let response = await getCatalogSyncHistories(cancelSource);

            if (response.code !== 200) {
                setError({open: true, errorHeader: "Error getting schedules", errorMessage: response.error ?? "Please try again later."});
            } else {
                setSchedules(response.data);
            }
        }

        const getConnectorListingStatuses = async () => {
            try {
                // General Product Status //
                let counts: ConnectorListingData = {};
                let searchStatus = "syncStatus";
                // let url = supplierCatalogSearch;
                let url = retailerCatalogSearch;


                // const productResponse = await axios.get(url + `?$top=20&$count=true&facets=['${searchStatus}']&$filter=(merchantId eq ${connectorId})`, {
                //     cancelToken: cancelSource.token
                // });
                const productResponse = await axios.get(url + `?merchantId=${connectorId}&$top=20&$count=true&facets=['${searchStatus}']`, {
                    cancelToken: cancelSource.token
                });

                counts["All"] = productResponse.data["@odata.count"];
                productResponse.data["facets"][searchStatus].forEach((value: any) => {
                    counts[value.value] = value.count;
                });

                // Product Errors //
                let errorCount = 0;
                // const errorResponse = await axios.get(url + `?$top=0&$count=true&facets=['errorMessage']&$filter=(merchantId eq ${connectorId})`, {
                //     cancelToken: cancelSource.token
                // });
                const errorResponse = await axios.get(url + `?merchantId=${connectorId}&$top=0&$count=true&facets=['errorMessage']`, {
                    cancelToken: cancelSource.token
                });
                errorResponse.data["facets"]["errorMessage"].forEach((value: any) => {
                    errorCount += value.count;
                });
                counts["Error"] = errorCount;

                // Filter Out Unnecessary Statuses //
                let formattedCounts: ConnectorListingData = {};
                Object.keys(counts ?? {}).forEach((key) => {
                    if (keptSyncStatuses.includes(key)) {
                        formattedCounts[key] = counts[key];
                    }
                });

                // Update State //
                setConnectorListingData(formattedCounts);
            } catch (error) {
                setError({open: true, errorHeader: "Error", errorMessage: "Error getting connector listing statuses."});
            }
        }

        const init = async () => {
            setConnectorLoading(true);
            await getConnectorListingStatuses();
            await getSchedules();
            await loadPreviousImport(profiles.find((profile: any) => profile.Partner === parseInt(String(connectorId ?? "000000"))));
            setConnectorLoading(false);
        }

        init();

        return () => {
            cancelSource.cancel();
            cancelSource = axios.CancelToken.source();
        }

        // eslint-disable-next-line
    }, []);

    return (
        <StatBox container style={{flex: 1, minWidth: "48%"}}>
            {(loading || connectorLoading) ? (
                <EnhancedCircularLoader/>
            ) : (
                <Grid container style={{flexDirection: "column"}}>
                    <Grid container style={{
                        display: "flex",
                        alignItems: "center"
                    }}>
                        <Typography style={{fontWeight: "bold", fontSize: "18px"}}>
                            {connectorName}
                        </Typography>

                        {isLive ? (
                            <Chip label="Live"
                                  style={{
                                      marginLeft: '0.5rem',
                                      backgroundColor: "#448044",
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: '8em'
                                  }}/>
                        ) : (
                            <Chip label="Action Required"
                                  style={{
                                      marginLeft: '0.5rem',
                                      backgroundColor: "#EE3224",
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: '8em'
                                  }}/>
                        )}
                    </Grid>

                    <Grid container style={{flex: 1, gap: "20px"}}>
                        <Grid container style={{flex: 1, alignItems: "center"}}>
                            {connectorListingData["All"] > 0 ? (
                                <DataBox title={""}
                                         datums={connectorListingData}
                                         colorMap={retailerColorMap}
                                         totalText={"Total # of Products"}
                                         type={"pie"}
                                         showLegend={true}
                                />
                            ) : (
                                <Alert severity="info" style={{minHeight: "100px", textAlign: "start"}}>
                                    Next Steps: Upload your initial file and complete your mappings.
                                    <br/>
                                    Click the data source button to continue.
                                </Alert>
                            )}
                        </Grid>

                        <Grid container style={{flex: 1, flexDirection: "column", alignItems: "flex-start"}}>
                            <Grid container style={{
                                flex: 1,
                                flexDirection: "column",
                                alignItems: "flex-start",
                                justifyContent: "center",
                            }}>
                                <DisableLink color='primary' component="a"
                                             style={{display: 'flex'}}
                                             underline="always"
                                             href={"/productonboardingcenter/import?partnerId=" + connectorId}>
                                    Data Source
                                </DisableLink>

                                <DisableLink color='primary' component="a"
                                             style={{display: 'flex'}}
                                             underline="always"
                                             href={"/productonboardingcenter/settings/channel-settings"}>
                                    Channel Settings
                                </DisableLink>

                                <Grid container style={{gap: "5px", alignItems: "flex-end"}}>
                                    <DisableLink
                                        href={"/productonboardingcenter/import?partnerId=" + connectorId + "&startingStep=2"}
                                        underline="always"
                                        disabled={!isLive}
                                        style={{fontWeight: "bold"}}>
                                        Category Mappings
                                    </DisableLink>

                                    {Object.keys(connectorProfile?.CategoryMappings ?? {}).length > 0 && (
                                        <MiniText>
                                            {Object.keys(connectorProfile?.CategoryMappings ?? {}).length} categories
                                        </MiniText>
                                    )}
                                </Grid>

                                <Grid container style={{gap: "5px", alignItems: "flex-end"}}>
                                    <DisableLink
                                        href={"/productonboardingcenter/import?partnerId=" + connectorId + "&startingStep=3"}
                                        underline="always"
                                        disabled={!isLive}
                                        style={{fontWeight: "bold"}}>
                                        Attribute Mappings
                                    </DisableLink>

                                    {Object.keys(connectorProfile?.AttributeMappings ?? {}).length > 0 && (
                                        <MiniText>
                                            {Object.keys(connectorProfile?.AttributeMappings ?? {}).length} attributes
                                        </MiniText>
                                    )}

                                    {Object.keys(connectorProfile?.AttributeMappingRules ?? {}).length > 0 && (
                                        <MiniText>
                                            {Object.keys(connectorProfile?.AttributeMappingRules ?? {}).length} rules
                                        </MiniText>
                                    )}
                                </Grid>

                                <Grid container style={{gap: "5px", alignItems: "flex-end"}}>
                                    <DisableLink
                                        href={"/productonboardingcenter/import?partnerId=" + connectorId + "&startingStep=4"}
                                        underline="always"
                                        disabled={!isLive}
                                        style={{fontWeight: "bold"}}>
                                        List Mappings
                                    </DisableLink>

                                    {Object.keys(connectorProfile?.ListMappings ?? {}).length > 0 && (
                                        <MiniText>
                                            {Object.keys(connectorProfile?.ListMappings ?? {}).length} lists
                                        </MiniText>
                                    )}
                                </Grid>

                                <DisableLink
                                    href={"/productonboardingcenter/catalog?catalog=events"}
                                    underline="always"
                                    style={{fontWeight: "bold"}}>
                                    Errors & Events
                                </DisableLink>
                            </Grid>

                            {isLive && (
                                <Grid container style={{justifyContent: "flex-start", alignItems: "center", gap: "10px", padding: "10px 0"}}>
                                    <Typography style={{fontWeight: "bold"}}>
                                        Next Scheduled
                                        Sync:
                                    </Typography>

                                    <Button
                                        component={"a"}
                                        href={"/scheduled-tasks"}
                                        style={{paddingRight: 5, paddingLeft: 5}}
                                    >
                                        {cronstrue.toString(schedules.find((s: any) => s.CoId === connectorId)?.Schedule ?? "* * * * *")}
                                    </Button>


                                    <Button
                                        variant={"contained"}
                                        color={"primary"}
                                        style={{minWidth: "80px"}}
                                        onClick={() => fireSync()}
                                        disabled={firingSync}
                                    >
                                        {firingSync ? (
                                            <EnhancedCircularLoader styles={{height: "20px", width: "20px"}}/>
                                        ) : "Sync Now"}
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid container style={{borderTop: "lightgray solid 1px", paddingTop: "5px"}}>
                        <Button
                            style={{flex: 1, minHeight: "84px"}}
                            component={"a"}
                            variant={"text"}
                            href={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=syncStatus%20eq%20Listed&q.1=merchantName%20eq%20" + connectorName}
                        >
                            <Grid container style={{flex: 1, flexDirection: "column", alignItems: "center", gap: "6px"}}>
                                <Typography style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    Total Products Synced
                                </Typography>

                                <Typography>
                                    {connectorListingData["Listed"] ?? "0"}
                                </Typography>
                            </Grid>
                        </Button>

                        <Button
                            style={{flex: 1, minHeight: "84px"}}
                            component={"a"}
                            variant={"text"}
                            href={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=syncStatus%20eq%20Listing%20Pending&q.1=merchantName%20eq%20" + connectorName}
                        >
                            <Grid container style={{flex: 1, flexDirection: "column", alignItems: "center", gap: "6px"}}>
                                <Typography style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    Total Products Pending Sync
                                </Typography>

                                <Typography>
                                    {connectorListingData["Listing Pending"] ?? "0"}
                                </Typography>
                            </Grid>
                        </Button>

                        <Button
                            style={{flex: 1, minHeight: "84px"}}
                            component={"a"}
                            variant={"text"}
                            href={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=syncStatus%20eq%20Listing%20Failed&q.1=merchantName%20eq%20" + connectorName}
                        >
                            <Grid container style={{flex: 1, flexDirection: "column", alignItems: "center", gap: "6px"}}>
                                <Typography style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    Total Products Listing Failed
                                </Typography>

                                <Typography>
                                    {connectorListingData["Listing Failed"] ?? "0"}
                                </Typography>
                            </Grid>
                        </Button>

                        <Button
                            style={{flex: 1, minHeight: "84px"}}
                            component={"a"}
                            variant={"text"}
                            href={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=errorMessage%20ne%20null&q.1=merchantName%20eq%20" + connectorName}
                        >
                            <Grid container style={{flex: 1, flexDirection: "column", alignItems: "center", gap: "6px"}}>
                                <Typography style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    Products With Errors
                                </Typography>

                                <Typography>
                                    {connectorListingData["Error"] ?? "0"}
                                </Typography>
                            </Grid>
                        </Button>
                    </Grid>
                </Grid>
            )}

            {didFireSync && (
                <GenericDialog
                    open={didFireSync}
                    setOpen={setDidFireSync}
                    title={"Sync Triggered"}
                    message={"The sync has been triggered for " + connectorName + "."}
                />
            )}
        </StatBox>
    );
}
